<template>
    <v-container class="my-5">
        <v-layout row wrap class="justify-center">
            <v-flex xs12>
                <!-- Header -->
                <v-card-title class="mb-4">
                    <v-btn icon @click="$router.go(-1)" class="mr-3">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <div>
                        <span class="headline font-weight-bold">Plan de Entrenamiento</span>
                        <div class="caption grey--text" v-if="member">{{ member.name }}</div>
                    </div>
                </v-card-title>

                <v-row v-if="currentAssignment && routine" class="mb-4">
                    <!-- Información de la Asignación -->
                    <v-col cols="12" md="8">
                        <v-card class="elevation-1">
                            <v-card-title>
                                <v-icon left>mdi-dumbbell</v-icon>
                                {{ routine.name }}
                            </v-card-title>
                            <v-card-text>
                                <div class="mb-3">
                                    <div class="subtitle-2 font-weight-bold mb-1">Descripción</div>
                                    <div class="body-2">{{ routine.description }}</div>
                                </div>

                                <v-divider class="my-3"></v-divider>

                                <v-row>
                                    <v-col cols="12" sm="6">
                                        <div class="caption grey--text">Coach Asignado</div>
                                        <div class="body-2 font-weight-bold">{{ getCoachName(currentAssignment.coach_id) }}</div>
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <div class="caption grey--text">Estado</div>
                                        <v-chip small :color="getStatusColor(currentAssignment.status)" text-color="white">
                                            {{ getStatusLabel(currentAssignment.status) }}
                                        </v-chip>
                                    </v-col>
                                </v-row>

                                <v-row class="mt-2">
                                    <v-col cols="12" sm="6">
                                        <div class="caption grey--text">Inicio</div>
                                        <div class="body-2">{{ formatDate(currentAssignment.start_date) }}</div>
                                    </v-col>
                                    <v-col cols="12" sm="6" v-if="currentAssignment.end_date">
                                        <div class="caption grey--text">Finalización</div>
                                        <div class="body-2">{{ formatDate(currentAssignment.end_date) }}</div>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <!-- Resumen de Progreso -->
                    <v-col cols="12" md="4">
                        <v-card class="elevation-1 text-center">
                            <v-card-text class="pa-4">
                                <div class="display-2 font-weight-bold primary--text mb-2">
                                    {{ completionPercentage }}%
                                </div>
                                <div class="subtitle-1 grey--text mb-3">Ejercicios Completados</div>
                                <v-linear-progress
                                    :value="completionPercentage"
                                    color="primary"
                                    class="mb-3">
                                </v-linear-progress>
                                <div class="caption">
                                    {{ currentAssignment.exercises_completed.length }} de {{ routine.exercises.length }} ejercicios
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Lista de Ejercicios -->
                <v-card class="elevation-1" v-if="routine">
                    <v-card-title>
                        <v-icon left>mdi-list-box</v-icon>
                        Ejercicios ({{ routine.exercises.length }})
                    </v-card-title>

                    <v-divider></v-divider>

                    <v-list v-if="routine.exercises.length > 0" dense>
                        <v-list-item
                            v-for="(exercise, idx) in routine.exercises"
                            :key="exercise.id"
                            class="mb-2">
                            <v-card class="w-100 mb-2" :class="isExerciseCompleted(exercise.id) ? 'green lighten-5' : ''">
                                <v-card-text class="pa-4">
                                    <v-row align="start" class="mb-2">
                                        <v-col cols="auto">
                                            <div class="body-2 font-weight-bold">
                                                {{ idx + 1 }}. {{ exercise.name }}
                                            </div>
                                        </v-col>
                                        <v-spacer></v-spacer>
                                        <v-col cols="auto">
                                            <v-checkbox
                                                :input-value="isExerciseCompleted(exercise.id)"
                                                @change="toggleExerciseCompletion(exercise.id)"
                                                class="ma-0">
                                                <template v-slot:label>
                                                    <span class="caption">{{ isExerciseCompleted(exercise.id) ? 'Completado' : 'Marcar como hecho' }}</span>
                                                </template>
                                            </v-checkbox>
                                        </v-col>
                                    </v-row>

                                    <div class="caption grey--text mb-2">{{ exercise.description }}</div>

                                    <v-row dense class="mb-3">
                                        <v-col cols="12" sm="6" md="3">
                                            <div class="caption grey--text">Series</div>
                                            <div class="body-2 font-weight-bold">{{ exercise.series }}</div>
                                        </v-col>
                                        <v-col cols="12" sm="6" md="3">
                                            <div class="caption grey--text">Repeticiones</div>
                                            <div class="body-2 font-weight-bold">{{ exercise.reps }}</div>
                                        </v-col>
                                        <v-col cols="12" sm="6" md="3">
                                            <div class="caption grey--text">Peso / Resistencia</div>
                                            <div class="body-2 font-weight-bold">{{ exercise.weight }}</div>
                                        </v-col>
                                        <v-col cols="12" sm="6" md="3">
                                            <div class="caption grey--text">Descanso</div>
                                            <div class="body-2 font-weight-bold">{{ exercise.restTime }}</div>
                                        </v-col>
                                    </v-row>

                                    <v-row v-if="exercise.notes" dense>
                                        <v-col cols="12">
                                            <v-alert
                                                dense
                                                outlined
                                                type="info"
                                                class="ma-0">
                                                <strong>Notas:</strong> {{ exercise.notes }}
                                            </v-alert>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-list-item>
                    </v-list>
                </v-card>

                <!-- Sin Asignaciones -->
                <v-card v-if="!currentAssignment" class="text-center pa-6">
                    <v-icon size="48" class="grey--text mb-2">mdi-alert-circle</v-icon>
                    <div class="subtitle-1 grey--text">Este socio no tiene rutinas asignadas</div>
                    <v-btn color="primary" text @click="$router.go(-1)" class="mt-2">
                        <v-icon left>mdi-arrow-left</v-icon>
                        Volver
                    </v-btn>
                </v-card>
            </v-flex>
        </v-layout>

        <!-- Snackbar -->
        <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
            {{ snackbarMessage }}
        </v-snackbar>
    </v-container>
</template>

<script>
import gymService from '@/services/gym.js'

export default {
  name: 'MemberWorkoutPlan',
  data: () => ({
    memberId: null,
    assignmentId: null,
    snackbar: false,
    snackbarMessage: '',
    snackbarColor: 'success'
  }),
  computed: {
    member () {
      return this.memberId ? gymService.getMemberById(this.memberId) : null
    },
    currentAssignment () {
      return this.assignmentId ? gymService.getAssignmentById(this.assignmentId) : null
    },
    routine () {
      return this.currentAssignment ? gymService.getRoutineById(this.currentAssignment.routine_id) : null
    },
    completionPercentage () {
      if (!this.routine || this.routine.exercises.length === 0) return 0
      return Math.round((this.currentAssignment.exercises_completed.length / this.routine.exercises.length) * 100)
    }
  },
  mounted () {
    // Obtener IDs de los query params
    this.memberId = parseInt(this.$route.query.member_id) || 1
    this.assignmentId = parseInt(this.$route.query.assignment_id) || 1
  },
  methods: {
    formatDate (dateStr) {
      const date = new Date(dateStr)
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    getCoachName (coachId) {
      const coach = gymService.getCoachById(coachId)
      return coach ? coach.name : 'Desconocido'
    },
    getStatusColor (status) {
      const colors = {
        activa: 'green',
        pausada: 'orange',
        completada: 'blue',
        cancelada: 'red'
      }
      return colors[status] || 'grey'
    },
    getStatusLabel (status) {
      const labels = {
        activa: 'Activa',
        pausada: 'Pausada',
        completada: 'Completada',
        cancelada: 'Cancelada'
      }
      return labels[status] || 'Desconocido'
    },
    isExerciseCompleted (exerciseId) {
      return this.currentAssignment.exercises_completed.includes(exerciseId)
    },
    toggleExerciseCompletion (exerciseId) {
      const isCompleted = this.isExerciseCompleted(exerciseId)
      if (isCompleted) {
        // Remover
        const index = this.currentAssignment.exercises_completed.indexOf(exerciseId)
        if (index > -1) {
          this.currentAssignment.exercises_completed.splice(index, 1)
        }
        this.snackbarMessage = 'Ejercicio marcado como pendiente'
      } else {
        // Agregar
        this.currentAssignment.exercises_completed.push(exerciseId)
        this.snackbarMessage = 'Ejercicio marcado como completado'
      }
      this.snackbarColor = 'success'
      this.snackbar = true
    }
  }
}
</script>

<style scoped>
.w-100 {
  width: 100%;
}
</style>
